import React from 'react';
import { cn, Switch } from '@crisellevaldez/speculo-ui';
import { HiChat, HiMail, HiPhone } from 'react-icons/hi';
import type { ChannelConsentSummary, ConsentChannel, ConsentStatus } from './consent.types';
import { CONSENT_DATE_LABEL, CONSENT_STATUS_LABEL } from './consentLabels';
import { formatTimeAgo } from '../../../../utils/handoffTime';
import { TimestampLabel } from '../../../common/TimestampLabel';
import { linkify } from '../../../../utils/linkify';

const CHANNEL_LABEL: Record<ConsentChannel, string> = {
  phone: 'Phone Calls',
  email: 'Email',
  sms: 'SMS',
};

/** Same icons the contact tab bar uses for Calls / SMS / Email. */
const CHANNEL_ICON: Record<ConsentChannel, React.ReactNode> = {
  phone: <HiPhone className="w-4 h-4" />,
  email: <HiMail className="w-4 h-4" />,
  sms: <HiChat className="w-4 h-4" />,
};

/** Plain-language line under the status, so agents don't have to interpret the flag. */
const STATUS_DETAIL: Record<ConsentChannel, Record<ConsentStatus, string>> = {
  phone: {
    opted_in: 'Safe to call · voice & voicemail',
    opted_out: 'Do not call · consent revoked',
    dnc: 'Do not call · suppressed by phone status',
    unknown: 'No consent on record',
  },
  email: {
    opted_in: 'Safe to send · email',
    opted_out: 'Do not email · consent revoked',
    dnc: 'Do not email · suppressed by email status',
    unknown: 'No consent on record',
  },
  sms: {
    opted_in: 'Safe to send · text messages',
    opted_out: 'Do not text · consent revoked',
    dnc: 'Do not text · suppressed by phone status',
    unknown: 'No consent on record',
  },
};

/**
 * Which field a DNC suppression is read from, so the note can say where the
 * standing came from. It names the source and stops there: a suppression is a
 * legal/deliverability state, and a consent surface should not tell an agent
 * how to lift one and resume outreach.
 */
const SUPPRESSION_FIELD: Record<ConsentChannel, string> = {
  phone: 'phone status',
  sms: 'phone status',
  email: 'email status',
};

const STATUS_DOT: Record<ConsentStatus, string> = {
  opted_in: 'bg-green-600',
  // Any not-opted-in standing reads neutral gray; green is reserved for consent.
  opted_out: 'bg-muted-foreground',
  dnc: 'bg-muted-foreground',
  unknown: 'bg-muted-foreground',
};

interface ChannelConsentCardProps {
  summary: ChannelConsentSummary;
  timeZone?: string;
  /** Omit to render the card read-only. */
  onToggle?: (channel: ConsentChannel, optedIn: boolean) => void;
  isUpdating?: boolean;
}

export const ChannelConsentCard: React.FC<ChannelConsentCardProps> = ({
  summary,
  timeZone,
  onToggle,
  isUpdating = false,
}) => {
  const { channel, status, changedAt, lastEvent, inheritedFromGlobalOptIn, grantable } = summary;

  const age = changedAt ? formatTimeAgo(changedAt) : null;
  const changeLabel = CONSENT_DATE_LABEL[status];
  const hasFooter = Boolean(
    (changeLabel && changedAt) ||
      lastEvent?.actor ||
      lastEvent?.note ||
      inheritedFromGlobalOptIn ||
      !grantable
  );

  return (
    <div className="flex flex-col gap-[18px] rounded-xl border border-foreground/30 bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-[9px] text-muted-foreground">
          {CHANNEL_ICON[channel]}
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">
            {CHANNEL_LABEL[channel]}
          </span>
        </div>
        {age && (
          <span className="text-[10px] tracking-[0.04em] text-muted-foreground/70">{age}</span>
        )}
      </div>

      <div className="flex flex-col gap-[5px]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', STATUS_DOT[status])} />
            <span className="truncate text-[17px] font-semibold tracking-[-0.02em] text-foreground">
              {CONSENT_STATUS_LABEL[status]}
            </span>
          </div>
          {onToggle && (
            <Switch
              size="sm"
              checked={status === 'opted_in'}
              // A suppression outranks the per-channel columns, so granting here
              // would write an opt-in the read model ignores.
              disabled={isUpdating || !grantable}
              aria-label={
                grantable
                  ? `${CHANNEL_LABEL[channel]} consent`
                  : `${CHANNEL_LABEL[channel]} consent locked by ${SUPPRESSION_FIELD[channel]}`
              }
              onChange={(e) => onToggle(channel, e.target.checked)}
            />
          )}
        </div>
        <span className="pl-[15px] text-xs text-muted-foreground">
          {STATUS_DETAIL[channel][status]}
        </span>
      </div>

      {hasFooter && (
        <div className="flex flex-col gap-[11px] border-t border-foreground/10 pt-[15px]">
          {changeLabel && changedAt && (
            <ConsentFact label={changeLabel}>
              {/* Reads as a Lead Info value here, not the smaller meta chrome
                  the component defaults to on activity rows. */}
              <TimestampLabel
                value={changedAt}
                timezone={timeZone}
                className="text-xs text-foreground"
              />
            </ConsentFact>
          )}
          {lastEvent?.actor && (
            <ConsentFact label="Updated by">
              <span>
                {lastEvent.actor}
                {lastEvent.actorRole && ` (${lastEvent.actorRole})`}
              </span>
            </ConsentFact>
          )}
          {lastEvent?.note && (
            <div className="border-l-2 border-foreground/20 py-px pl-[11px] text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {linkify(lastEvent.note, { shortenLabels: true })}
            </div>
          )}
          {!grantable && (
            <div className="border-l-2 border-foreground/20 py-px pl-[11px] text-xs text-muted-foreground [overflow-wrap:anywhere]">
              Set by this contact&apos;s {SUPPRESSION_FIELD[channel]}, which overrides consent.
              It can&apos;t be changed here.
            </div>
          )}
          {inheritedFromGlobalOptIn && (
            <div className="border-l-2 border-foreground/20 py-px pl-[11px] text-xs text-muted-foreground [overflow-wrap:anywhere]">
              Inherited from the contact&apos;s opt-in record. Toggling sets consent for this
              channel on its own.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ConsentFact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[11px] text-muted-foreground">{label}</span>
    <span className="text-xs text-foreground">{children}</span>
  </div>
);
